import "../types/index.js";

import {
  bindActions,
  bindActionsLateLoaded,
  bindAttributes,
  bindServices,
  bindSubscriptions,
  bindTargets,
} from "../utilities/bindings.js";
import {
  getElement,
  getElements,
  getOptionalElement,
} from "../utilities/elements.js";
import {
  getActualCssValue,
  getNumberCssValue,
  getStringCssValue,
} from "../utilities/styles.js";

const elementGetters = [getElement, getElements, getOptionalElement];

/** Convenience methods for getting CSS property values to define on the component */
const styleGetters = [getActualCssValue, getNumberCssValue, getStringCssValue];

/**
 * Parent class for all DickTok custom UI components.
 *
 * DickTok UI components register custom elements using the
 * [Web Components API](https://developer.mozilla.org/en-US/docs/Web/API/Web_components).
 */
export class DickTokComponent extends HTMLElement {
  #disconnectController = new AbortController();

  /**
   * Services to initialize during connectedCallback, see `services/services.js`.
   *
   * Services are singleton classes exposing global functionality. They are
   * bound to the component, for example `"LoggingService"` binds a `.loggingService`
   * property to access the global service. All services required in a class or
   * its superclasses are available.
   *
   * @type {string[]}
   */
  static services = ["LoggingService"];

  /**
   * Subscriptions to initialize during connectedCallback, see `events/subscriptions.js`.
   *
   * Subscriptions are named global EventTargets. They are bound to the component,
   * for example `"FeedEvents"` binds a `feedEvents` property to access the
   * global EventTarget. This can be used to publish or subscribe to events using
   * `dispatchEvent()` and `addEventListener()`. All subscriptions required in a
   * class or its superclasses are available.
   *
   * @type {string[]}
   */
  static subscriptions = [];

  /**
   * Attributes to initialize during connectedCallback.
   *
   * Configuring attributes defines typed accessors for the attribute that also
   * update the DOM. For example, `{name: "foo", type: "string"}` binds `.foo` and
   * `.foo=`, and these update both the property and the DOM attribute.
   *
   * As the DOM attribute value is stored as a string, the `type` is used to coerce
   * the value when accessed in JavaScript. The accessors throw an error if the value
   * is of the wrong type or is missing (except for boolean attributes).
   *
   * When `optional` is true, a `.hasFoo` property is defined, and the accessor
   * returns `null` instead of throwing an error if the attribute is missing.
   *
   * If there is a `fooChanged(oldValue, newValue)` method defined on the component,
   * it is called when the attribute changes.
   *
   * @type {AttributeDefinition[]}
   */
  static attributes = [];

  /**
   * Target elements to find during connectedCallback.
   *
   * Targets are child elements with the `data-target` attribute set. If a target
   * is defined but not found in the child elements during the connectedCallback,
   * it raises an error. Accessors are created for each target, for example,
   * `"foo"` will find an element with `data-target="foo"` and create a `.fooTarget`
   * property that returns the element.
   *
   * @type {Array<string>}
   */
  static targets = [];

  /** @type {Map<string, HTMLElement>} Refs to target elements */
  targets = new Map();

  constructor() {
    super();
    for (const getter of [...elementGetters, ...styleGetters]) {
      Object.defineProperty(this, getter.name, {
        value: function (...args) {
          return getter(this, ...args);
        },
      });
    }
  }

  connectedCallback() {
    bindServices(this);
    bindSubscriptions(this);
    bindAttributes(this);
    bindTargets(this);
    /* See methods for actions documentation. */
    bindActions(this);
    bindActionsLateLoaded(this);
  }

  disconnectedCallback() {
    this.#disconnectController.abort(`${this.class} element disconnected`);
  }

  /**
   * If this callback is implemented, it's called when the child elements change.
   * Note that for standalone components, this is called when the shadow DOM
   * changes, otherwise this is called when the light DOM changes.
   *
   * To observe changes in the light DOM from a standalone component, add a
   * listener to the "slotchange" event on a HTMLSlotElement instead.
   *
   * For example, using actions:
   * ```html
   * <slot data-action="slotchange:dicktok-component->countSlottedElements">
   * </slot>
   * ```
   *
   * @abstract
   * @param {MutationRecord} mutation
   */
  childrenChangedCallback(mutation) {}

  get class() {
    return this.constructor.name;
  }

  get disconnectSignal() {
    return this.#disconnectController.signal;
  }

  log(...params) {
    this.loggingService.log(this.class, ...params);
  }

  error(...params) {
    this.loggingService.error(this.class, ...params);
  }
}
